<script>
import GoogleMapsMixin from "@/mixins/google-maps.mixin.ts";
import { MasksMixin } from "@/mixins/masks.mixin.ts";
import ValidationMixin from "@/mixins/validation.mixin.ts";
import { Countries, CountriesEnum } from "@/constants/countries.ts";
import OmniSelect from "@/components/selects/OmniSelect.vue";
import OmniTextField from "@/components/omni/OmniTextField.vue";
import { MINIMUM_ZIP_LENGTH } from "@/constants/zip.ts";
import { CanadaStates, UsStates } from "@/constants/states.ts";
import { ONLY_ZEROS } from "@/constants/regexp.ts";

export default {
  name: "AddressField",
  components: { OmniTextField, OmniSelect },
  mixins: [GoogleMapsMixin, MasksMixin, ValidationMixin],
  props: {
    modelValue: Object,
    label: {
      type: String,
      default: "",
    },
    modelPrefix: {
      type: String,
      default: "",
    },
    modelSuffix: {
      type: String,
      default: "",
    },
    hideLocation: Boolean,
    disabled: Boolean,
  },
  emits: ["update:modelValue"],
  data() {
    return {
      loading: false,
      countries: Countries,
    };
  },
  computed: {
    addressLineName() {
      return this.asCamelCase([this.modelPrefix, "addressLine", this.modelSuffix]);
    },
    addressCityName() {
      return this.asCamelCase([this.modelPrefix, "addressCity", this.modelSuffix]);
    },
    addressStateName() {
      return this.asCamelCase([this.modelPrefix, "addressState", this.modelSuffix]);
    },
    addressZipCodeName() {
      return this.asCamelCase([this.modelPrefix, "addressZipCode", this.modelSuffix]);
    },
    addressCountryName() {
      return this.asCamelCase([this.modelPrefix, "addressCountry", this.modelSuffix]);
    },
    addressLocationName() {
      return this.asCamelCase([this.modelPrefix, "addressLocation", this.modelSuffix]);
    },
    addressLine: {
      get() {
        return this.modelValue[this.addressLineName];
      },
      set(value) {
        this.update(this.addressLineName, value);
      },
    },
    addressCity: {
      get() {
        return this.modelValue[this.addressCityName];
      },
      set(value) {
        this.update(this.addressCityName, value);
      },
    },
    addressState: {
      get() {
        return this.modelValue[this.addressStateName];
      },
      set(value) {
        this.update(this.addressStateName, value);
      },
    },
    addressZipCode: {
      get() {
        return this.modelValue[this.addressZipCodeName];
      },
      set(value) {
        this.update(this.addressZipCodeName, value);
      },
    },
    addressCountry: {
      get() {
        return this.modelValue[this.addressCountryName];
      },
      set(value) {
        this.update(this.addressCountryName, value);
      },
    },
    hasLocation() {
      return this.location.lat !== null && this.location.lon !== null;
    },
    location() {
      if (!this.modelValue.addressLocation) {
        return { lat: null, lon: null };
      }
      return { lat: this.modelValue.addressLocation.lat, lon: this.modelValue.addressLocation.lon };
    },
  },
  watch: {
    addressState: {
      handler(val) {
        if (!val) {
          return;
        }

        let country = null;

        if (UsStates.includes(val)) {
          country = CountriesEnum.USA;
        } else if (CanadaStates.includes(val)) {
          country = CountriesEnum.CANADA;
        }

        if (!country) {
          return;
        }

        this.update("addressCountry", country);
      },
    },
  },
  methods: {
    async validateZipCode(zipCode) {
      if (!zipCode.length) {
        return true;
      }

      const invalidMessage = "Wrong ZIP code";
      const isAllZeros = ONLY_ZEROS.test(zipCode);
      const isReachedMinimum = zipCode.length > MINIMUM_ZIP_LENGTH;

      if (!isAllZeros && isReachedMinimum) {
        this.loading = true;

        const resp = await this.$api.geo.searchPostalCode(zipCode);

        this.loading = false;

        if (resp.success && resp.data.length > 0) {
          const result = resp.data[0];
          const data = {};
          data[this.addressLocationName] = { lon: result.lon, lat: result.lat };
          if (!this.addressState && result.address.state) {
            data[this.addressStateName] = result.address.state.substring(0, 2).toUpperCase();
          }
          if (!this.addressCity && result.address.city) {
            data[this.addressCityName] = result.address.city;
          }

          if (!this.addressCountry && result.address.country) {
            data[this.addressCountryName] = result.address.country;
          }

          this.updateAll(data);
          return true;
        }

        return invalidMessage;
      } else {
        this.loading = false;
        return invalidMessage;
      }
    },
    asCamelCase(chunks) {
      return chunks
        .filter((chunk) => chunk !== "" && chunk !== undefined && chunk !== null)
        .map((chunk, index) => (index === 0 ? chunk : this.capitalize(chunk)))
        .join("");
    },
    capitalize(text) {
      return text.charAt(0).toUpperCase() + text.slice(1);
    },
    update(key, value) {
      this.$emit("update:modelValue", { ...this.modelValue, [key]: value });
    },
    updateAll(object) {
      this.$emit("update:modelValue", { ...this.modelValue, ...object });
    },
  },
};
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-3">
      <div class="caption text-grey-darken-1">{{ label }}</div>
      <div v-if="!hideLocation" class="d-flex align-center">
        <v-icon
          :class="{
            'text-grey-darken-1': !hasLocation,
            'text-green': hasLocation,
          }"
          class="mr-2"
          size="14"
          >mdi-map-marker-radius-outline
        </v-icon>
        <a
          v-if="hasLocation"
          :href="getPlaceUrl(location)"
          class="text-decoration-underline text-green caption"
          target="_blank"
        >
          Lat:
          {{ location.lat }}
          Lon:
          {{ location.lon }}
        </a>
        <span v-if="!hasLocation" class="text-decoration-underline text-grey-darken-1 caption"> Lat: ... Lon: ...</span>
      </div>
      <slot name="label:suffix"></slot>
    </div>

    <v-row>
      <v-col>
        <omni-text-field
          v-model="addressLine"
          :disabled="disabled"
          :rules="[requiredValidator]"
          data-qa="address_line"
          label="Address line"
          required
        />
      </v-col>
    </v-row>
    <v-row class="mt-0">
      <v-col cols="4">
        <omni-text-field
          v-model="addressCity"
          :disabled="disabled"
          :rules="[requiredValidator]"
          data-qa="address_city"
          label="City"
          required
        />
      </v-col>
      <v-col cols="3">
        <omni-text-field
          v-model="addressState"
          v-maska="stateShortMask"
          :disabled="disabled"
          :rules="[requiredValidator, stateValidator]"
          data-qa="address_state"
          label="State"
          required
        />
      </v-col>
      <v-col cols="3">
        <omni-text-field
          v-model="addressZipCode"
          :disabled="disabled"
          :loading="loading"
          :rules="[validateZipCode]"
          data-qa="address_zip_code"
          label="Zip code"
        />
      </v-col>
      <v-col cols="2">
        <omni-select
          v-model="addressCountry"
          :disabled="disabled"
          :items="countries"
          :rules="[requiredValidator]"
          data-qa="address_country"
          label="Country"
        />
      </v-col>
    </v-row>
  </div>
</template>
